/*
  Thanks Noah Smith from:
  https://codepen.io/noahsmith/pen/porWdvM

  I've just added a background image following ChatGPT instructions

  Click and drag to make ripples. 
  
  Original WebGL version in p5.js: https://editor.p5js.org/noahsmith50/sketches/yZz6cK8kk
  Based on https://editor.p5js.org/codingtrain/sketches/tYXtzNSl
  
  Works on mobile too (iOS 15 Safari)
  Uses 16-bit floating point textures for storing the water heights.
  
  Uncomment #define CALCULATE_LIGHTING to make it perform crude lighting calculations

*/


let RESIZE_TO_WINDOW, w, h, canvas, gl, backgroundTexture, waterDrops, animation_frame_request_id

let animationTimeoutId = null;

waterDrops = []

// must be <= 1
let dampening = 0.900; 

// must be >= 1
let disturbanceRadius = 1;

const rippleShader = {
    program: null,
};

const displayShader = {
    program: null,
};

let positionBuffer;
let mainVAO;

/*
    Three buffers are needed. One will be the buffer that
    is currently being rendered to and the other two
    will be the previous two frames. It will run in a 
    circular manner. For example, when buffer 0 is rendered to,
    the previous frame buffer is buffer 2 and the one before that
    is buffer 1. Buffer 1 is then rendered to, with buffer 0
    as the previous buffer and buffer 2 before that one.
    
    This is necessary because the buffers aren't copied when acting
    as a source to the shader. The input and output textures to a 
    shader cannot be the same or it will throw an error
*/
const buffers = [ {}, {}, {} ];

let mouseX = 0;
let mouseY = 0;
let mouseIsPressed = false;

let statePhase = 0;

const rippleShaderSource = {
    vert: `#version 300 es
precision highp float;

in vec3 a_position;
out vec2 v_texCoord;

void main() {
  vec2 clipSpaceCoord = a_position.xy * 2.0 - 1.0;
  v_texCoord = a_position.xy;
                
  gl_Position = vec4(clipSpaceCoord, 0.0, 1.0);
}`,
  
    // render ripple heights to the red channel of the float texture

    frag: `#version 300 es

    #define MAX_DROPS 8

precision highp float;

uniform sampler2D u_texInputA;
uniform sampler2D u_texInputB;
uniform vec2 u_texInputRes;
uniform int u_dropCount;
uniform vec2 u_dropPositions[MAX_DROPS];
uniform float u_dampening;
uniform float u_disturbanceRadius;

in vec2 v_texCoord; // or "uv"/"st"
out vec4 fragColor;

// offsets a texture coordinate ranging from 0 to 1 by 
// an amount measured in pixels (converted to a small fraction of 1)
vec2 offsetTexCoord(vec2 texCoord, vec2 offsetInPixels) {
  vec2 texCoordWithOffset = texCoord + offsetInPixels / u_texInputRes;
  return texCoordWithOffset;
}

void main() {
  // https://editor.p5js.org/codingtrain/sketches/tYXtzNSl
                                
  float thisHeightB = texture(u_texInputB, v_texCoord).r; 
  float leftHeightA = texture(u_texInputA, offsetTexCoord(v_texCoord, vec2(-1.0, 0.0))).r;
  float rightHeightA = texture(u_texInputA, offsetTexCoord(v_texCoord, vec2(1.0, 0.0))).r;
  float topHeightA = texture(u_texInputA, offsetTexCoord(v_texCoord, vec2(0.0, 1.0))).r;
  float bottomHeightA = texture(u_texInputA, offsetTexCoord(v_texCoord, vec2(0.0, -1.0))).r;

  float newHeight = (leftHeightA + rightHeightA + topHeightA + bottomHeightA) / 2.0 - thisHeightB;
  newHeight *= u_dampening;  
                
  for (int i = 0; i < MAX_DROPS; i++) {
    if (i >= u_dropCount) break;

    vec2 normalizedDrop = u_dropPositions[i];

    if (length((v_texCoord - normalizedDrop) * u_texInputRes) < u_disturbanceRadius) {
        newHeight = 10.0;
    }
  }
                              
  fragColor = vec4(vec3(clamp(newHeight, -4.0, 4.0)), 1.0);
}`,
  
};

// this shader is used to render the texture onto the canvas

const displayShaderSource = {
    vert: `#version 300 es
precision highp float;

in vec3 a_position;
out vec2 v_texCoord;

void main() {
  vec2 clipSpaceCoord = a_position.xy * 2.0 - 1.0;
  v_texCoord = a_position.xy;
                
  gl_Position = vec4(clipSpaceCoord, 0.0, 1.0);
}`,

    // can be used for post-processing effects such as lighting and reflections
    frag: `#version 300 es
precision highp float;
uniform sampler2D u_texInput;
uniform vec2 u_texRes;
uniform sampler2D u_backgroundImage;

// uncomment this to add (awful) lighting
//#define CALCULATE_LIGHTING

in vec2 v_texCoord; // or "uv"/"st"
out vec4 fragColor;

vec2 offsetTexCoord(vec2 texCoord, vec2 offsetInPixels) {
  vec2 texCoordWithOffset = texCoord + offsetInPixels / u_texRes;
  return texCoordWithOffset;
}

void main() {
  float height = texture(u_texInput, v_texCoord).r;

  float deltaHeightTop = texture(u_texInput, offsetTexCoord(v_texCoord, vec2(0.0, 1.0))).r - height;
  float deltaHeightBottom = texture(u_texInput, offsetTexCoord(v_texCoord, vec2(0.0, -1.0))).r - height;
  float deltaHeightRight = texture(u_texInput, offsetTexCoord(v_texCoord, vec2(1.0, 0.0))).r - height;
  float deltaHeightLeft = texture(u_texInput, offsetTexCoord(v_texCoord, vec2(-1.0, 0.0))).r - height;

  vec2 surfaceNormalProjection = (vec2(deltaHeightRight - deltaHeightLeft, deltaHeightBottom - deltaHeightTop));
   
  vec2 offset = surfaceNormalProjection * 0.5; // distortion
  vec2 distortedCoords = v_texCoord + offset;
  vec3 texColor = texture(u_backgroundImage, distortedCoords).rgb;


  #ifdef CALCULATE_LIGHTING
  fragColor = vec4(waterColor * (0.0 / 8.0 + 0.5), 1.0);
  #else
  fragColor = vec4(texColor, 1.0);
  #endif

  // yes this is awful
  #ifdef CALCULATE_LIGHTING
  float ls = dot(surfaceNormalProjection, (vec2(0.4, 0.4))) / 2.0;
  fragColor.xyz += pow(abs(ls + 0.1), 2.4) + ls / 2.0;
  #endif
}`,
  
};



function init() {
    waterDrops = []
    // compile the shaders
    rippleShader.program = createShaderProgram(rippleShaderSource.vert, rippleShaderSource.frag);
    if (!rippleShader.program)
        return console.error('Cannot create "ripple" shader program');

    displayShader.program = createShaderProgram(displayShaderSource.vert, displayShaderSource.frag);
    if (!displayShader.program)
        return console.error('Cannot create "display" shader program');

    setCanvasSize(w, h);
    gl.useProgram(rippleShader.program);
  
    gl.getExtension('EXT_color_buffer_float');

    // disable depth testing and back-face culling
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);

    // not necessary
    mainVAO = gl.createVertexArray();
    gl.bindVertexArray(mainVAO);

    // create and send the geometry (2 triangles forming a quad) to the GPU
    rippleShader.positionAttributeLocation = gl.getAttribLocation(rippleShader.program, "a_position");
    positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    const positions = [
        -1, -1,
        -1, +1,
        +1, +1,
        +1, +1,
        +1, -1,
        -1, -1,
    ];
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(rippleShader.positionAttributeLocation);

    // point the "display" shader to the same geometry
    gl.useProgram(displayShader.program);
    gl.enableVertexAttribArray(displayShader.positionAttributeLocation);
    gl.vertexAttribPointer(displayShader.positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);

    gl.useProgram(rippleShader.program);

    for (let i = 0; i < buffers.length; i++) {
        setupBuffer(buffers[i]);
        gl.clearColor(0.5, 0.5, 0.5, 1.0);
        gl.bindFramebuffer(gl.FRAMEBUFFER, buffers[i].frameBuffer);
        gl.clear(gl.COLOR_BUFFER_BIT);
    }
  
    // the following could be added to the object automatically
    // by using WebGL API functions

    rippleShader.texALocation = gl.getUniformLocation(rippleShader.program, "u_texInputA");
    rippleShader.texBLocation = gl.getUniformLocation(rippleShader.program, "u_texInputB");
    rippleShader.texResLocation = gl.getUniformLocation(rippleShader.program, "u_texInputRes");
    rippleShader.dropCountLocation = gl.getUniformLocation(rippleShader.program, "u_dropCount");
    rippleShader.dropPositionsLocation = gl.getUniformLocation(rippleShader.program, "u_dropPositions");
    rippleShader.dampeningLocation = gl.getUniformLocation(rippleShader.program, "u_dampening");
    rippleShader.disturbanceRadiusLocation = gl.getUniformLocation(rippleShader.program, "u_disturbanceRadius");

    displayShader.texResLocation = gl.getUniformLocation(displayShader.program, "u_texRes");
    displayShader.texLocation = gl.getUniformLocation(displayShader.program, "u_texInput");

    animation_frame_request_id = requestAnimationFrame(render);
}

function setupBuffer(buffer) {
    buffer.colorAttachment = gl.createTexture();
    buffer.frameBuffer = gl.createFramebuffer();

    gl.bindTexture(gl.TEXTURE_2D, buffer.colorAttachment); 
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R16F, w, h, 0, gl.RED, gl.FLOAT, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    
    gl.bindFramebuffer(gl.FRAMEBUFFER, buffer.frameBuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, buffer.colorAttachment, 0);
}

function resizeBuffers() {
    for (let i = 0; i < buffers.length; i++) {
        const buffer = buffers[i];
        gl.bindTexture(gl.TEXTURE_2D, buffer.colorAttachment); 
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.R16F, w, h, 0, gl.RED, gl.FLOAT, null);
    }
}

function render() {
    if(!gl){
        return
    }

    gl.useProgram(rippleShader.program);

    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.vertexAttribPointer(rippleShader.positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);

    gl.uniform2fv(rippleShader.texResLocation, [w, h]);
    gl.uniform1i(rippleShader.texALocation, 0);
    gl.uniform1i(rippleShader.texBLocation, 1);

    // send the dampening float value and disturbance radius to the GPU as a uniform only once
    gl.uniform1f(rippleShader.dampeningLocation, dampening);
    gl.uniform1f(rippleShader.disturbanceRadiusLocation, disturbanceRadius);

    gl.bindFramebuffer(gl.FRAMEBUFFER, buffers[statePhase].frameBuffer);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, buffers[(statePhase + (3 - 1)) % 3].colorAttachment);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, buffers[(statePhase + (3 - 2)) % 3].colorAttachment);

    const flatPositions = waterDrops.flat();

    gl.uniform1i(rippleShader.dropCountLocation, waterDrops.length);

    if(flatPositions.length){
        gl.uniform2fv(rippleShader.dropPositionsLocation, flatPositions);
    }


    gl.drawArrays(gl.TRIANGLES, 0, 6);

    waterDrops.length = 0

    renderTextureToCanvas(buffers[statePhase].colorAttachment);

    /* 
        Increment the phase and wrap it around when it's above two.
        This value indicates the texture to render to, and the previous two
        represent the source textures.
    */
    statePhase++;
    if (statePhase > 2)
        statePhase = 0;

    animationTimeoutId = setTimeout(()=>{
        animation_frame_request_id = requestAnimationFrame(render)
    }, 40);
}

// render the texture passed to this function on the canvas
function renderTextureToCanvas(texture) {
    gl.useProgram(displayShader.program);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.vertexAttribPointer(displayShader.positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);

    gl.uniform2fv(displayShader.texResLocation, [w, h]);
    gl.uniform1i(displayShader.texALocation, 0);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);

    gl.uniform1i(gl.getUniformLocation(displayShader.program, "u_backgroundImage"), 1);

    gl.activeTexture(gl.TEXTURE1);
    //
    gl.bindTexture(gl.TEXTURE_2D, backgroundTexture);

    gl.drawArrays(gl.TRIANGLES, 0, 6);
}

/*
// boilerplate stuff:

document.addEventListener('mousemove', e => {
    mouseX = e.clientX;
    mouseY = e.clientY;
});

document.addEventListener('touchmove', e => {
    mouseX = e.touches[0].clientX;
    mouseY = e.touches[0].clientY;
});

document.addEventListener('mousedown', e => {
    mouseIsPressed = true;
});

document.addEventListener('mouseup', e => {
    mouseIsPressed = false;
});

document.addEventListener('touchstart', e => {
    mouseIsPressed = true;
    mouseX = e.touches[0].clientX;
    mouseY = e.touches[0].clientY;
});

document.addEventListener('touchend', e => {
    mouseIsPressed = false;
});

*/
function createShaderProgram(vertexSource, fragSource) {
    function compileShader(gl, type, source) {
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        const success = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
        if (success) {
            return shader;
        }

        const e = gl.getShaderInfoLog(shader);
        console.error(e);
        gl.deleteShader(shader);
    }

    function createProgram(gl, vertexShader, fragmentShader) {
        const program = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);
        const success = gl.getProgramParameter(program, gl.LINK_STATUS);
        if (success) {
            return program;
        }

        const e = gl.getProgramInfoLog(program);
        console.error(e);
        gl.deleteProgram(program);
    }

    const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
    const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragSource);
    const prog = createProgram(gl, vertexShader, fragmentShader);

    return prog;
}

export function setCanvasSize() {
    if(!canvas){
        return
    }

    const pw = w;
    const ph = h;

    w = canvas.width;
    h = canvas.height;

    gl.viewport(0, 0, w, h);

    if (pw != w || ph != h){
        resizeBuffers() || console.log('rsb');
    }
}



function loadBackgroundImage(image_src, onLoad){
    const image = new Image();
    image.src = image_src;  // ✅ Ruta a la imagen

    image.onload = function () {
        gl.bindTexture(gl.TEXTURE_2D, backgroundTexture);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        onLoad()
    };
}

export function startRipple(canvas_element, image_src){
    RESIZE_TO_WINDOW = false;

    canvas = canvas_element

    w = canvas_element.width || 800
    h = canvas_element.height || 600 

    gl = canvas.getContext('webgl2');

    backgroundTexture = gl.createTexture()

    loadBackgroundImage(image_src, ()=>{
        if (gl) {
            init();  
        } else {
            alert('No WebGL 2 Support');
        }
    })

    return setCanvasSize
}

export function destroyRipple() {
    console.log('Cleaning up ripple resources');

    if(!gl){
        return
    }

    if (animationTimeoutId !== null) {
        clearTimeout(animationTimeoutId);
        animationTimeoutId = null;
    }

    if(animation_frame_request_id){
        cancelAnimationFrame(animation_frame_request_id)
    }

    // Delete shaders/programs
    if (rippleShader.program) {
        gl.deleteProgram(rippleShader.program)
    }

    if (displayShader.program) {
        gl.deleteProgram(displayShader.program)
    }

    // Delete textures
    if (backgroundTexture) {
        gl.deleteTexture(backgroundTexture)
    }

    for (let i = 0; i < buffers.length; i++) {
        if (buffers[i].colorAttachment) {
            gl.deleteTexture(buffers[i].colorAttachment)
        }
    }

    // Delete framebuffers
    for (let i = 0; i < buffers.length; i++) {
        if (buffers[i].frameBuffer) {
            gl.deleteFramebuffer(buffers[i].frameBuffer)
        }
    }

    // Delete buffer and VAO
    if (positionBuffer) {
        gl.deleteBuffer(positionBuffer)
    };

    if (mainVAO) {
        gl.deleteVertexArray(mainVAO)
    };

    // Clear state
    canvas = null;
    gl = null;
    backgroundTexture = null;
    positionBuffer = null;
    mainVAO = null;
    waterDrops = [];
}

export function waterDrop(x, y){
    if(!canvas){
        return
    }
    //clearTimeout(press_timeout)

    waterDrops.push([
        x / w,
        1.0 - y/h
    ])
}

export function setDampeningAndRadius(_dampening, _radius){
    dampening = _dampening
    disturbanceRadius = _radius
}